import React, {useState, useRef, useCallback, useEffect, useId} from 'react';
import {Button, Text, Icon, WithTooltip} from '@albert-io/atomic';

import useOnClickOutside from 'lib/hooks/useOnClickOutside';
import classnames from 'classnames';
import {useShallow} from 'zustand/react/shallow';
import {
  addMediaText,
  useQuestionTranslationStore
} from 'client/components/QuestionTypes/shared/TranslationSection';

import QuestionTypeStore from '../../../../../../QuestionType.store';
import {closeOnFocusOut} from '../closeOnFocusOut';

import {ImageUploadButton} from './UploadButtons/ImageUploadButton.react';
import {AudioUploadButton} from './UploadButtons/AudioUploadButton.react';

const UNAVAILABLE_MESSAGE = 'Media upload is only available in assignments.';
const NOT_A_TAB_STOP = {tabIndex: undefined};

interface AddMediaButtonProps {
  disabled?: boolean;
  tabIndex?: number;
  'data-toolbar-control'?: boolean;
}

export const AddMediaButton = ({disabled = false, tabIndex, ...rest}: AddMediaButtonProps) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const buttonRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const addMediaRef = useRef<HTMLButtonElement>(null);
  const [activeCursor, setActiveCursor] = useState<number>(0);

  // Media uploads require a draft guess, which shouldNotSaveDraft() already
  // captures the conditions for (no assignment, e.g. standalone FRQ practice;
  // not a student; no session). Disable uploads instead of 403ing silently.
  const isMediaUploadSupported = !QuestionTypeStore.shouldNotSaveDraft();
  const unavailableMessageId = useId();

  const {showTranslations} = useQuestionTranslationStore(
    useShallow((state) => ({
      showTranslations: state.showTranslations && state.translationSettings.question
    }))
  );

  const handleClickOutside = useCallback(() => {
    if (showDropdown) {
      setShowDropdown(false);
    }
  }, [showDropdown]);

  useOnClickOutside(buttonRef, handleClickOutside);

  const closeMenu = () => {
    const focusWasInMenu = dropdownRef.current?.contains(document.activeElement);
    setShowDropdown(false);
    if (focusWasInMenu) {
      addMediaRef.current?.focus();
    }
  };

  const handleMenuKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!showDropdown || !dropdownRef.current?.contains(event.target as Node)) {
      return;
    }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveCursor((current) => (current === 0 ? 1 : 0));
    } else if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
      closeMenu();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      closeMenu();
    }
  };

  useEffect(() => {
    if (showDropdown && dropdownRef.current) {
      const selected = dropdownRef.current.children[activeCursor] as HTMLElement;
      if (selected) {
        selected.focus();
      }
    }
  }, [activeCursor, showDropdown]);

  return (
    <div
      className='written-submission__add-media__container'
      ref={buttonRef}
      onBlur={(event) => closeOnFocusOut(event, buttonRef.current, () => setShowDropdown(false))}
      onKeyDown={handleMenuKeyDown}
    >
      <WithTooltip
        enabled={!isMediaUploadSupported}
        focusable
        content={UNAVAILABLE_MESSAGE}
        wrapperProps={NOT_A_TAB_STOP}
      >
        <Button
          ref={addMediaRef}
          className={classnames('gap-x1', {'a-button--disabled': !isMediaUploadSupported})}
          color='secondary'
          variant='outlined'
          disabled={disabled}
          aria-disabled={!isMediaUploadSupported || undefined}
          aria-describedby={isMediaUploadSupported ? undefined : unavailableMessageId}
          onClick={() => {
            if (isMediaUploadSupported) {
              setActiveCursor(0);
              setShowDropdown(!showDropdown);
            }
          }}
          tabIndex={tabIndex}
          {...rest}
        >
          <Text>{showTranslations ? addMediaText.button : 'Add Media'}</Text>
          <Icon icon='caret-down' />
        </Button>
      </WithTooltip>
      {!isMediaUploadSupported && (
        <span id={unavailableMessageId} className='sr-only'>
          {UNAVAILABLE_MESSAGE}
        </span>
      )}

      {isMediaUploadSupported && (
        <MediaDropdown
          setShowDropdown={(show: boolean) => (show ? setShowDropdown(true) : closeMenu())}
          showDropdown={showDropdown}
          dropdownRef={dropdownRef}
          activeCursor={activeCursor}
          showTranslation={showTranslations}
        />
      )}
    </div>
  );
};

interface MediaDropdownProps {
  setShowDropdown: (boolean) => void;
  showDropdown: boolean;
  dropdownRef: React.RefObject<HTMLDivElement | null>;
  activeCursor: number;
  showTranslation?: boolean;
}

const MediaDropdown = ({
  setShowDropdown,
  showDropdown,
  dropdownRef,
  activeCursor,
  showTranslation = false
}: MediaDropdownProps) => {
  const className = classnames(
    'written-submission__media-dropdown__container',
    showDropdown ? 'flex' : 'hidden'
  );

  return (
    <div className={className} ref={dropdownRef}>
      <ImageUploadButton
        showTranslation={showTranslation}
        setShowDropdown={setShowDropdown}
        tabIndex={activeCursor === 0 ? 0 : -1}
      />
      <AudioUploadButton
        showTranslation={showTranslation}
        setShowDropdown={setShowDropdown}
        tabIndex={activeCursor === 1 ? 0 : -1}
      />
    </div>
  );
};
